import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["content"]
  static values = { 
    text: String,
    position: { type: String, default: "top" }
  }

  connect() {
    this.tooltip = null
    this.hideTimeout = null
    this.showTimeout = null
    
    // Bind event handlers
    this.handleScroll = this.reposition.bind(this)
    this.handleResize = this.reposition.bind(this)
    
    // Add global event listeners for repositioning
    window.addEventListener('scroll', this.handleScroll, true)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    this.hide()
    
    // Clean up event listeners
    window.removeEventListener('scroll', this.handleScroll, true)
    window.removeEventListener('resize', this.handleResize)
    
    // Clear any pending timeouts
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
    }
  }

  show(event) {
    // Clear any pending timeouts
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
    }

    // Delay showing tooltip slightly for better UX
    this.showTimeout = setTimeout(() => {
      // Create or update tooltip
      if (!this.tooltip) {
        this.createTooltip()
      }

      // Position the tooltip
      this.positionTooltip(event.currentTarget || this.element)
      
      // Show tooltip with animation
      requestAnimationFrame(() => {
        if (this.tooltip) {
          this.tooltip.classList.remove('opacity-0', 'invisible')
          this.tooltip.classList.add('opacity-100', 'visible')
        }
      })
    }, 200) // 200ms delay before showing
  }

  hide() {
    // Clear show timeout if hiding
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
      this.showTimeout = null
    }
    
    if (this.tooltip) {
      this.tooltip.classList.remove('opacity-100', 'visible')
      this.tooltip.classList.add('opacity-0', 'invisible')
      
      // Remove tooltip after animation
      this.hideTimeout = setTimeout(() => {
        if (this.tooltip && this.tooltip.parentNode) {
          this.tooltip.parentNode.removeChild(this.tooltip)
          this.tooltip = null
        }
      }, 200)
    }
  }

  // For mobile: toggle on click
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.tooltip && this.tooltip.classList.contains('visible')) {
      this.hide()
    } else {
      this.show(event)
      
      // Auto-hide after 3 seconds on mobile
      if ('ontouchstart' in window) {
        setTimeout(() => this.hide(), 3000)
      }
    }
  }

  createTooltip() {
    this.tooltip = document.createElement('div')
    this.tooltip.className = 'fixed z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg pointer-events-none transition-opacity duration-200 opacity-0 invisible max-w-xs'
    this.tooltip.textContent = this.textValue
    
    // Add arrow
    const arrow = document.createElement('div')
    arrow.className = this.getArrowClasses()
    this.tooltip.appendChild(arrow)
    
    document.body.appendChild(this.tooltip)
  }

  positionTooltip(target) {
    if (!this.tooltip) return

    const rect = target.getBoundingClientRect()
    const tooltipRect = this.tooltip.getBoundingClientRect()
    
    let top, left

    switch (this.positionValue) {
      case 'bottom':
        top = rect.bottom + 8
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
        break
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2)
        left = rect.left - tooltipRect.width - 8
        break
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2)
        left = rect.right + 8
        break
      default: // top
        top = rect.top - tooltipRect.height - 8
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
    }

    // Keep tooltip within viewport
    const padding = 8
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding))
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding))

    this.tooltip.style.top = `${top}px`
    this.tooltip.style.left = `${left}px`
  }

  getArrowClasses() {
    const baseClasses = 'absolute w-0 h-0 border-4 border-transparent'
    
    switch (this.positionValue) {
      case 'bottom':
        return `${baseClasses} -top-2 left-1/2 -translate-x-1/2 border-b-gray-900`
      case 'left':
        return `${baseClasses} -right-2 top-1/2 -translate-y-1/2 border-l-gray-900`
      case 'right':
        return `${baseClasses} -left-2 top-1/2 -translate-y-1/2 border-r-gray-900`
      default: // top
        return `${baseClasses} -bottom-2 left-1/2 -translate-x-1/2 border-t-gray-900`
    }
  }

  // Handle window scroll/resize
  reposition() {
    if (this.tooltip && this.tooltip.classList.contains('visible')) {
      this.positionTooltip(this.element)
    }
  }
}